import React, { useEffect, useState, useContext, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import AdminNav from "../../components/admin/AdminNav";
import { GroupContext } from "../../context/GroupContext";
import api from "../../utils/api";
import ConfirmationModal from '../../components/common/ConfirmationModal'; // Adjust path if needed
import InfoModal from '../../components/common/InfoModal';

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];
const PORTION_OPTIONS = ["Full", "Half", "Quarter"];
const EPSILON = 0.001;

export default function EditGroup() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { replaceGroup, getGroup, fetchGroup, groups: contextGroups } = useContext(GroupContext);

  const currentUser = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      return u;
    } catch {
      return {};
    }
  })();
  const role = (currentUser?.role || "").toLowerCase();
  const isSupplierAdmin = role === "supplieradmin";
  const scopedSupplierId = currentUser?.supplierId || null;

  const [group, setGroup] = useState({
    name: "",
    supplierId: "",
    state: "",
    animal: "",
    costPerPortion: "",
    totalCost: "",
    remainingCost: "",
    portionsAvailable: [],
    status: "open",
    dateClosed: null,
    updatedAt: null,
  });
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const lastSyncedRef = useRef(null);

  // Info Modal state for results
  const [infoModal, setInfoModal] = useState({
    isOpen: false,
    title: "",
    message: "",
  });

  // Confirmation Modal state for update confirmation
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const effectiveStatus = (() => {
    const rem = Number(group.remainingCost ?? 0);
    if (group.status === "closed") return "closed";
    if (rem <= EPSILON) return "full";
    return "open";
  })();

  // --- Load data and sync omitted for brevity (same as your current code) ---
  // Copy the useEffect loading logic and syncing exactly as you have it
  // I'll skip pasting here for brevity, you can keep yours unchanged

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const res = await api.get(`/api/suppliers`);
        let supplierList = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data.suppliers)
          ? res.data.suppliers
          : [];

        if (isSupplierAdmin && scopedSupplierId) {
          supplierList = supplierList.filter(
            (s) => String(s._id) === String(scopedSupplierId)
          );
        }
        setSuppliers(supplierList);

        if (supplierList.length === 0) {
          toast.warn("No suppliers found. You may need to create one first.");
        }

        let contextGroup = getGroup ? getGroup(id) : null;
        if (!contextGroup) {
          contextGroup = fetchGroup ? await fetchGroup(id) : await api.get(`/api/groups/${id}`).then(r => r.data.group || r.data);
        }

        if (contextGroup) {
          const groupSupplierId =
            typeof contextGroup.supplierId === "string"
              ? contextGroup.supplierId
              : contextGroup.supplierId?._id;

          if (
            isSupplierAdmin &&
            scopedSupplierId &&
            String(groupSupplierId) !== String(scopedSupplierId)
          ) {
            toast.error("You do not have permission to edit this group.");
            navigate(-1);
            return;
          }

          setGroup({
            name: contextGroup.name || "",
            supplierId: groupSupplierId || "",
            state: contextGroup.state || "",
            animal: contextGroup.animal || "",
            costPerPortion: contextGroup.costPerPortion || "",
            totalCost: contextGroup.totalCost || "",
            remainingCost:
              contextGroup.remainingCost != null
                ? contextGroup.remainingCost
                : contextGroup.totalCost || "",
            portionsAvailable: contextGroup.portionsAvailable || [],
            status: contextGroup.status || "open",
            dateClosed: contextGroup.dateClosed || null,
            updatedAt: contextGroup.updatedAt || null,
          });
          lastSyncedRef.current = contextGroup.updatedAt;
        } else {
          toast.error("Group not found.");
          navigate(-1);
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load data.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id, contextGroups, fetchGroup, getGroup, isSupplierAdmin, scopedSupplierId, navigate]);

  useEffect(() => {
    if (!getGroup) return;
    const contextGroup = getGroup(id);
    if (!contextGroup) return;
    if (contextGroup.updatedAt && contextGroup.updatedAt !== lastSyncedRef.current) {
      setGroup({
        name: contextGroup.name || "",
        supplierId:
          typeof contextGroup.supplierId === "string"
            ? contextGroup.supplierId
            : contextGroup.supplierId?._id || "",
        state: contextGroup.state || "",
        animal: contextGroup.animal || "",
        costPerPortion: contextGroup.costPerPortion || "",
        totalCost: contextGroup.totalCost || "",
        remainingCost:
          contextGroup.remainingCost != null
            ? contextGroup.remainingCost
            : contextGroup.totalCost || "",
        portionsAvailable: contextGroup.portionsAvailable || [],
        status: contextGroup.status || "open",
        dateClosed: contextGroup.dateClosed || null,
        updatedAt: contextGroup.updatedAt || null,
      });
      lastSyncedRef.current = contextGroup.updatedAt;
    }
  }, [getGroup, id, contextGroups]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "costPerPortion") {
      const remaining = parseFloat(group.remainingCost);
      if (!isNaN(remaining) && parseFloat(value) > remaining) {
        toast.error("Cost per portion cannot exceed remaining cost.");
        return setGroup((prev) => ({ ...prev, [name]: remaining.toString() }));
      }
    }

    if (name === "remainingCost") {
      const cost = parseFloat(group.costPerPortion);
      if (!isNaN(cost) && parseFloat(value) < cost) {
        toast.error("Remaining cost cannot be less than cost per portion.");
      }
    }

    if (name === "status") {
      const newStatus = value;
      setGroup((prev) => ({
        ...prev,
        status: newStatus,
        remainingCost:
          newStatus === "full" || newStatus === "closed" ? "0" : prev.remainingCost,
        dateClosed: newStatus === "closed" ? new Date().toISOString() : prev.dateClosed,
      }));
      return;
    }

    setGroup((prev) => ({ ...prev, [name]: value }));
  };

  const togglePortion = (portion) => {
    setGroup((prev) => {
      const selected = prev.portionsAvailable.includes(portion);
      return {
        ...prev,
        portionsAvailable: selected
          ? prev.portionsAvailable.filter((p) => p !== portion)
          : [...prev.portionsAvailable, portion],
      };
    });
  };

  // This replaces handleSubmit on form submit:
  const handleFormSubmit = (e) => {
    e.preventDefault();
    setConfirmModalOpen(true); // open confirm modal before actually submitting
  };

  // Confirmed submit handler
  const handleConfirmSubmit = async () => {
    setConfirmModalOpen(false);

    const numericRemaining = parseFloat(group.remainingCost);
    const numericTotalCost = parseFloat(group.totalCost);

    if (numericRemaining > numericTotalCost) {
      setInfoModal({
        isOpen: true,
        title: "Validation Error",
        message: "Remaining Cost to Share cannot exceed Total Cost",
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.put(`/api/groups/${id}`, group);
      const updatedGroup = response.data.group || response.data;

      replaceGroup(updatedGroup);

      setGroup((prev) => ({
        ...prev,
        ...updatedGroup,
        remainingCost:
          updatedGroup.remainingCost != null
            ? updatedGroup.remainingCost
            : updatedGroup.totalCost || "",
      }));

      setInfoModal({
        isOpen: true,
        title: "Success",
        message: `Group "${updatedGroup.name}" updated successfully`,
      });

      setTimeout(() => {
        setInfoModal({ isOpen: false, title: "", message: "" });
        navigate(-1);
      }, 2000);
    } catch (err) {
      console.error(err);
      setInfoModal({
        isOpen: true,
        title: "Error",
        message: "An error occurred while updating the group.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <p className="text-center mt-10 text-gray-600">Loading group data...</p>
    );

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNav />
      <div className="max-w-md mx-auto mt-10 p-6 bg-white shadow-md rounded">
        <h1 className="text-2xl font-bold mb-6 text-center">Edit Group</h1>
        <form onSubmit={handleFormSubmit} className="space-y-5">
          {/* Form fields unchanged, same as your original */}

          {/* Group Name */}
          <div>
            <label className="block text-sm font-medium mb-1">Group Name</label>
            <input
              name="name"
              value={group.name}
              onChange={handleChange}
              className="border p-2 w-full rounded"
              required
            />
          </div>

          {/* Supplier */}
          <div>
            <label className="block text-sm font-medium mb-1">Supplier</label>
            <select
              name="supplierId"
              value={group.supplierId}
              onChange={handleChange}
              className="border p-2 w-full rounded"
              required
              disabled={isSupplierAdmin}
              title={
                isSupplierAdmin
                  ? "Supplier Admins cannot change supplier"
                  : undefined
              }
            >
              <option value="">Select Supplier</option>
              {suppliers.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {/* State */}
          <div>
            <label className="block text-sm font-medium mb-1">State</label>
            <select
              name="state"
              value={group.state}
              onChange={handleChange}
              className="border p-2 w-full rounded"
              required
            >
              <option value="">Select State</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          {/* Animal */}
          <div>
            <label className="block text-sm font-medium mb-1">Animal</label>
            <select
              name="animal"
              value={group.animal}
              onChange={handleChange}
              className="border p-2 w-full rounded"
              required
            >
              <option value="">Select Animal</option>
              <option value="Beef">Beef</option>
              <option value="Lamb">Lamb</option>
              <option value="Goat">Goat</option>
              <option value="Sheep">Sheep</option>
            </select>
          </div>

          {/* Total Cost (readonly) */}
          <div>
            <label className="block text-sm font-medium mb-1">Total Cost ($)</label>
            <input
              name="totalCost"
              value={group.totalCost}
              readOnly
              className="border p-2 w-full rounded bg-gray-100"
            />
          </div>

          {/* Remaining Cost */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Remaining Cost to Share ($)
            </label>
            <input
              name="remainingCost"
              type="number"
              value={group.remainingCost}
              onChange={handleChange}
              className="border p-2 w-full rounded"
              required
            />
          </div>

          {/* Cost Per Portion (readonly) */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Cost Per Portion ($)
            </label>
            <input
              name="costPerPortion"
              type="number"
              value={group.costPerPortion}
              readOnly
              className="border p-2 w-full rounded bg-gray-100"
            />
          </div>

          {/* Portions Available toggle buttons */}
          <div>
            <label className="block text-sm font-medium mb-1">Portions Available</label>
            <div className="flex gap-2">
              {PORTION_OPTIONS.map((portionOption) => {
                const selected = group.portionsAvailable.includes(portionOption);
                return (
                  <button
                    type="button"
                    key={portionOption}
                    onClick={() => togglePortion(portionOption)}
                    className={`px-3 py-1 rounded border text-sm ${
                      selected
                        ? "bg-green-600 text-white border-green-600"
                        : "bg-white text-gray-800 border-gray-400"
                    }`}
                  >
                    {portionOption}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Status (effective) */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Status (effective)
            </label>
            <div className={`inline-block p-2 rounded ${getStatusClasses(effectiveStatus)}`}>
              {effectiveStatus}
            </div>
          </div>

          {/* Status (stored) */}
          <div>
            <label className="block text-sm font-medium mb-1">Status (stored)</label>
            <select
              name="status"
              value={group.status}
              onChange={handleChange}
              className={`p-2 w-full rounded ${getStatusClasses(group.status)}`}
            >
              <option value="open">Open</option>
              <option value="full">Full</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          {/* Date Closed */}
          <div>
            <label className="block text-sm font-medium mb-1">Date Closed</label>
            <input
              type="text"
              value={group.dateClosed ? new Date(group.dateClosed).toLocaleString() : ""}
              readOnly
              className="border p-2 w-full rounded bg-gray-100"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              {submitting ? "Updating..." : "Update Group"}
            </button>
          </div>
        </form>

        <ToastContainer position="top-right" autoClose={3000} theme="colored" />

        {/* Info Modal for success/errors */}
        <InfoModal
          isOpen={infoModal.isOpen}
          title={infoModal.title}
          message={infoModal.message}
          onClose={() => setInfoModal({ isOpen: false, title: "", message: "" })}
        />

        {/* Confirmation Modal for update confirmation */}
        <ConfirmationModal
          isOpen={confirmModalOpen}
          title="Confirm Update"
          message="Are you sure you want to update this group?"
          onConfirm={handleConfirmSubmit}
          onClose={() => setConfirmModalOpen(false)}
        />
      </div>
    </div>
  );
}

function getStatusClasses(status) {
  switch (status) {
    case "open":
      return "bg-green-100 text-green-800 border border-green-300";
    case "full":
      return "bg-yellow-100 text-yellow-800 border border-yellow-300";
    case "closed":
      return "bg-gray-200 text-gray-800 border border-gray-300";
    default:
      return "";
  }
}